import { useRef, useState } from "react";
import { Camera, RefreshCw, Upload } from "lucide-react";

interface Props {
  label: string;
  captured: boolean;
  url?: string;
  onCapture: (file: File) => void;
  required?: boolean;
}

export function PhotoCapture({ label, captured, url, onCapture, required = true }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleClick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onCapture(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) onCapture(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  return (
    <>
      {/* Mobile: capture="environment" opens rear camera directly */}
      {/* Desktop: capture is ignored, file picker opens normally */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleChange}
      />
      <button
        type="button"
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative overflow-hidden rounded-xl border-2 transition-all active:scale-[0.97] min-h-[120px] w-full ${
          dragOver ? "border-primary bg-primary/10 scale-[1.02]" : ""
        }`}
        style={{ padding: 0 }}
      >
        {captured && url ? (
          <>
            <img
              src={url}
              alt={label}
              className="w-full h-full object-cover min-h-[120px]"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black/55 px-2 py-1.5 flex items-center justify-between gap-1">
              <span className="text-[10px] font-semibold text-white leading-tight truncate">{label}</span>
              <RefreshCw className="h-3 w-3 text-white/80 shrink-0" />
            </div>
            <div className="absolute top-2 left-2 bg-success/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">
              ✓ Captured
            </div>
          </>
        ) : (
          <div className={`flex flex-col items-center justify-center p-5 h-full min-h-[120px] border-dashed rounded-xl transition-colors ${
            dragOver
              ? "border-primary bg-primary/5"
              : captured
              ? "border-success/40 bg-success/5"
              : "border-border hover:border-primary/40 hover:bg-primary/5"
          }`}>
            {required && !captured && (
              <span className="absolute top-2 right-2 text-[9px] font-bold text-destructive uppercase tracking-wider">Required</span>
            )}
            {dragOver ? (
              <>
                <Upload className="h-8 w-8 text-primary mb-2" />
                <span className="text-xs font-medium text-primary">Drop to upload</span>
              </>
            ) : (
              <>
                <Camera className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <span className="text-xs font-medium text-center leading-tight text-muted-foreground">{label}</span>
                <span className="text-[10px] text-muted-foreground/60 mt-1 hidden sm:block">or drop image here</span>
              </>
            )}
          </div>
        )}
      </button>
    </>
  );
}
